import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

interface Args {
  user: User | null;
  enabled: boolean; // typically: isUnlocked && !!user && !!entityId
  contentType: "premium_guide" | "digital_resource";
  entityId: string | null | undefined;
  title: string | null | undefined;
  slug?: string | null;
  featuredImage?: string | null;
  /** CSS selector for the element whose scroll depth represents reading progress */
  contentSelector?: string;
  /** CSS selector for headings used as resume anchors (must have id) */
  anchorSelector?: string;
}

const COMPLETE_THRESHOLD = 95;
const SAVE_INTERVAL_MS = 4000;

/**
 * Auto-enrolls a signed-in, unlocked user into premium_content_progress and
 * tracks their scroll depth + nearest visible h2 anchor so they can resume.
 */
export function usePremiumProgressTracker({
  user,
  enabled,
  contentType,
  entityId,
  title,
  slug,
  featuredImage,
  contentSelector,
  anchorSelector = "h2[id], h3[id]",
}: Args) {
  const seededRef = useRef<string | null>(null);
  const stateRef = useRef({ pct: 0, anchor: "" as string | null });
  const dirtyRef = useRef(false);

  // Seed a row at 0% the first time this user views this unlocked item.
  useEffect(() => {
    if (!enabled || !user || !entityId || !title) return;
    const key = `${user.id}:${contentType}:${entityId}`;
    if (seededRef.current === key) return;
    seededRef.current = key;

    (async () => {
      // Check if a row already exists; if so just touch last_accessed_at.
      const { data: existing } = await supabase
        .from("premium_content_progress" as any)
        .select("id, progress_percentage, last_section, last_scroll_pct")
        .eq("user_id", user.id)
        .eq("content_type", contentType)
        .eq("entity_id", entityId)
        .maybeSingle();

      if (existing) {
        stateRef.current.pct = (existing as any).last_scroll_pct ?? (existing as any).progress_percentage ?? 0;
        stateRef.current.anchor = (existing as any).last_section ?? null;
        await supabase
          .from("premium_content_progress" as any)
          .update({ last_accessed_at: new Date().toISOString() })
          .eq("id", (existing as any).id);
      } else {
        await supabase.from("premium_content_progress" as any).insert({
          user_id: user.id,
          content_type: contentType,
          entity_id: entityId,
          title,
          slug: slug ?? null,
          featured_image: featuredImage ?? null,
          progress_percentage: 0,
          last_scroll_pct: 0,
        });
      }
    })();
  }, [enabled, user?.id, contentType, entityId, title, slug, featuredImage]);

  // Scroll tracker
  useEffect(() => {
    if (!enabled || !user || !entityId) return;

    const onScroll = () => {
      const el = contentSelector ? document.querySelector(contentSelector) : null;
      const rect = el?.getBoundingClientRect();
      const docTop = window.scrollY;
      const docHeight = el && rect
        ? (el as HTMLElement).offsetTop + (el as HTMLElement).offsetHeight - window.innerHeight
        : document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? Math.max(0, Math.min(100, Math.round((docTop / docHeight) * 100))) : 0;

      // nearest anchor above viewport center
      const center = window.innerHeight / 2;
      let nearest: string | null = stateRef.current.anchor;
      document.querySelectorAll<HTMLElement>(anchorSelector).forEach((node) => {
        const r = node.getBoundingClientRect();
        if (r.top <= center) nearest = node.id || nearest;
      });

      if (pct > stateRef.current.pct || nearest !== stateRef.current.anchor) {
        stateRef.current.pct = Math.max(stateRef.current.pct, pct);
        stateRef.current.anchor = nearest;
        dirtyRef.current = true;
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const flush = async () => {
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      const pct = stateRef.current.pct;
      const completed = pct >= COMPLETE_THRESHOLD;
      await supabase
        .from("premium_content_progress" as any)
        .update({
          progress_percentage: completed ? 100 : pct,
          last_scroll_pct: pct,
          last_section: stateRef.current.anchor,
          last_accessed_at: new Date().toISOString(),
          completed_at: completed ? new Date().toISOString() : null,
        })
        .eq("user_id", user.id)
        .eq("content_type", contentType)
        .eq("entity_id", entityId);
    };

    const interval = window.setInterval(flush, SAVE_INTERVAL_MS);
    const onHide = () => { void flush(); };
    window.addEventListener("beforeunload", onHide);
    document.addEventListener("visibilitychange", onHide);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.clearInterval(interval);
      window.removeEventListener("beforeunload", onHide);
      document.removeEventListener("visibilitychange", onHide);
      void flush();
    };
  }, [enabled, user?.id, contentType, entityId, contentSelector, anchorSelector]);
}
